import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { usePager } from "../components/Pagination";
import PageHeader from "../components/PageHeader";
import { Plus, Pencil, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, Input } from "./Suppliers";
import { useAuth } from "../context/AuthContext";

const EMPTY = { email: "", name: "", designation: "", role_id: "", password: "", active: true };

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [tab, setTab] = useState("active"); // active | trash
  const isSA = !!me?.role?.is_super_admin;

  const load = async () => {
    const [u, r] = await Promise.all([
      api.get(tab === "trash" ? "/users?include_deleted=true" : "/users"),
      api.get("/roles"),
    ]);
    const rows = u.data.filter((x) => tab === "trash" ? !!x.deleted_at : !x.deleted_at);
    setUsers(rows); setRoles(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const roleName = (id) => roles.find((x) => x.id === id)?.name || "—";

  const save = async () => {
    try {
      if (editing) await api.put(`/users/${editing}`, form);
      else await api.post("/users", form);
      setOpen(false);
      toast.success(editing ? "User updated" : "User created");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      // Non-optimistic: close the modal FIRST, reload, THEN toast — eliminates
      // the double-render flicker the user flagged.
      const victim = confirmDel;
      setConfirmDel(null);
      await api.delete(`/users/${victim.id}`);
      await load();
      toast.success(`Moved ${victim.email} to trash (recoverable for 30 days)`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
  };

  const doRestore = async (u) => {
    try {
      await api.post(`/users/${u.id}/restore`);
      toast.success(`Restored ${u.original_email || u.email}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Restore failed"); }
  };

  // Wave-J pagination
  const { visibleRows: pagedUsers, controls: userControls } = usePager(users, 10);

  return (
    <div data-testid="users-page">
      <PageHeader title="Users" subtitle="Operator accounts and role assignment"
        right={<button onClick={() => { setForm(EMPTY); setEditing(null); setOpen(true); }} className="bg-[#1565C0] hover:bg-[#0D2B5E] text-white text-sm px-4 py-2 rounded-sm flex items-center gap-2 uppercase tracking-wider font-semibold" data-testid="new-user-btn"><Plus size={14}/> New User</button>}
      />
      <div className="p-8 space-y-3">
        <div className="flex gap-2">
          {[["active", "Active Users"], ["trash", "Trash (30-day restore)"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`users-tab-${k}`}
              className={`px-4 py-2 text-xs uppercase tracking-widest font-bold rounded-sm border ${tab === k ? "bg-[#0D2B5E] text-white border-[#0D2B5E]" : "bg-white text-[#0D2B5E] border-[#EEEEEE]"}`}>{l}</button>
          ))}
        </div>
        <div className="bg-white border border-[#EEEEEE] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Email</th>
                <th className="text-left px-4 py-2">Role</th>
                <th className="text-left px-4 py-2">{tab === "trash" ? "Deleted" : "Active"}</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedUsers.map((u, i) => (
                <tr key={u.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"}>
                  <td className="px-4 py-2 font-medium">{u.name}</td>
                  <td className="px-4 py-2 font-mono text-xs">
                    {tab === "trash" ? (
                      <span title={u.email}><span className="text-[#C62828] line-through">{u.original_email || u.email}</span></span>
                    ) : u.email}
                  </td>
                  <td className="px-4 py-2 uppercase tracking-widest text-xs text-[#1565C0]">{roleName(u.role_id)}</td>
                  <td className="px-4 py-2 text-xs">
                    {tab === "trash"
                      ? <span className="text-[#C62828]">{(u.deleted_at || "").slice(0, 10)} by {u.deleted_by || "—"}</span>
                      : (u.active ? "Yes" : "No")}
                  </td>
                  <td className="px-4 py-2 text-right space-x-2">
                    {tab === "active" && (
                      <>
                        <button onClick={() => { setForm({ ...u, password: "" }); setEditing(u.id); setOpen(true); }} className="text-[#1565C0]" title="Edit" data-testid={`edit-user-${u.email}`}><Pencil size={14}/></button>
                        {isSA && u.id !== me?.id && (
                          <button onClick={() => setConfirmDel(u)} className="text-[#C62828]" title="Move to trash" data-testid={`delete-user-${u.email}`}><Trash2 size={14}/></button>
                        )}
                      </>
                    )}
                    {tab === "trash" && isSA && (
                      <button onClick={() => doRestore(u)} className="text-xs bg-[#2E7D32] text-white px-3 py-1 rounded-sm uppercase tracking-wider font-bold inline-flex items-center gap-1" title="Restore user" data-testid={`restore-user-${u.original_email || u.email}`}><RotateCcw size={12}/>Restore</button>
                    )}
                  </td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-[#0D2B5E]/50">{tab === "trash" ? "No deleted users." : "No active users."}</td></tr>}
            </tbody>
          </table>
          {userControls}
        </div>
      </div>

      {open && (
        <Dialog title={editing ? "Edit User" : "New User"} onClose={() => setOpen(false)}>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="u-name"/>
            <Input label="Designation" value={form.designation || ""} onChange={(v) => setForm({ ...form, designation: v })} testId="u-designation"
              placeholder="e.g. Plant Head, Procurement Manager, Managing Director"/>
            <Input label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="u-email"/>
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Role</label>
              <select value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })} data-testid="u-role" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm">
                <option value="">Select role…</option>
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <Input label={editing ? "Password (leave blank to keep)" : "Password"} type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })}/>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
            <button onClick={save} className="px-4 py-2 bg-[#1565C0] text-white text-sm rounded-sm font-semibold tracking-wider uppercase" data-testid="save-user-btn">Save</button>
          </div>
        </Dialog>
      )}

      {confirmDel && (
        <Dialog title="Move User to Trash" onClose={() => setConfirmDel(null)}>
          <div data-testid="confirm-delete-user">
            <div className="text-sm text-[#0D2B5E] mb-2">
              Move this user to <span className="font-bold text-[#C62828]">Trash</span>?
            </div>
            <div className="bg-[#F5F5F5] border-l-4 border-[#C62828] p-3 mb-4">
              <div className="font-bold text-[#0D2B5E]">{confirmDel.name}</div>
              <div className="font-mono text-xs text-[#1565C0]">{confirmDel.email}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 mt-1">{roleName(confirmDel.role_id)}</div>
            </div>
            <div className="text-xs text-[#0D2B5E]/70 mb-4 leading-5">
              They will lose access immediately. Their audit-log entries and existing GRN / DC / inspection records remain intact.
              You have <strong>30 days</strong> to restore the user from the Trash tab.
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
              <button onClick={doDelete} className="px-4 py-2 bg-[#C62828] text-white text-sm rounded-sm font-semibold tracking-wider uppercase" data-testid="confirm-delete-btn">Move to Trash</button>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
}
